import { z } from 'zod';
import { LotIdSchema, PlayerIdSchema } from '../ids';
import { CompanyIdSchema } from './company';
import { ItemAmountsSchema } from './items';
import { WorldRectSchema } from './place';
import { BuildingSchema, ProjectSchema } from './town';

/**
 * What a lot's land is to its owner (HOME-CLAIM): bought from the power that holds the land
 * (`paid`), raised there without leave (`squat`, RV-30: the newcomer's way), on land nobody
 * holds (`free`), or fallen to ruin, unpaid or pulled down (`ruin`, RV-22: what it holds is
 * anyone's). A public contract: never rename, only add.
 */
export const LOT_STATUSES = ['paid', 'squat', 'free', 'ruin'] as const;
export const LotStatusSchema = z.enum(LOT_STATUSES);
export type LotStatus = z.infer<typeof LotStatusSchema>;

/**
 * Whose a lot is (plan E6, HG-1): a character's (its home lot) or a company's, whose members act on
 * it by rank. A company's name is null once it was dissolved (its lots kept as ruins, LC-6).
 */
export const LotOwnerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('player'), playerId: PlayerIdSchema, displayName: z.string() }),
  z.object({ kind: z.literal('company'), companyId: CompanyIdSchema, name: z.string().nullable() }),
]);
export type LotOwner = z.infer<typeof LotOwnerSchema>;

/** Whether a lot is this character's own (a company's lots are judged by rank, plan E6). */
export const ownsLot = (owner: LotOwner, playerId: string): boolean =>
  owner.kind === 'player' && owner.playerId === playerId;

/** A lot's owner as data to show: a character's name, or its company's (null once dissolved). */
export const lotOwnerName = (owner: LotOwner): string | null =>
  owner.kind === 'player' ? owner.displayName : owner.name;

/**
 * A lot as everyone near it sees it (HOME technique 1): where it lies (world tiles), whose it is,
 * its standing, whether its hearth is lit (TI-24: a lot without one is no home and claims
 * nothing), and what stands on it, in tiles of the lot.
 */
export const LotStateSchema = z.object({
  id: LotIdSchema,
  owner: LotOwnerSchema,
  rect: WorldRectSchema,
  status: LotStatusSchema,
  /** The power that holds its land; null on free land. */
  land: z.string().nullable(),
  claimed: z.boolean(),
  structures: z.array(BuildingSchema),
  /** Structures being raised on it (BUILD), in tiles of the lot. */
  projects: z.array(ProjectSchema),
  /** Tiles of the lot whose tree was felled (RV-27): open ground, in tiles of the lot. */
  cleared: z.array(z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()])),
  /** Game time its canon is paid until (a paid lot, plan C7); null otherwise. */
  paidUntil: z.number().int().nonnegative().nullable(),
  /** Game time it falls to ruin if nothing changes (in arrears, or noticed without leave). */
  fallsAt: z.number().int().nonnegative().nullable(),
  /**
   * The small site it covers (plan C10): its place's id, and whether its custody sleeps (held, or
   * fallen and not back yet: SS-2). Null for a lot traced by its holder.
   */
  site: z.object({ place: z.string(), held: z.boolean() }).nullable(),
  /** A character's temporary camp (plan C8): when it expires unless its owner comes by (CM-1). */
  camp: z.object({ expiresAt: z.number().int().nonnegative() }).nullable(),
  /**
   * A character's siege camp (SIEGE H2b, RM-4): when it expires unless its owner comes by. Absent
   * on any other lot.
   */
  siege: z.object({ expiresAt: z.number().int().nonnegative() }).optional(),
  /**
   * Where it stands among its holder's standing lots, in founding order (1 for the first): a
   * company's later lots owe more (plan E6, LC-4). Absent, 1.
   */
  standing: z.number().int().positive().optional(),
  /**
   * Since when its pieces' upkeep and its hearth's keep went unpaid (durability DR-2, DR-7): they
   * decay from then until paid. Absent, kept.
   */
  neglectedSince: z.number().int().nonnegative().optional(),
  hearthNeglectedSince: z.number().int().nonnegative().optional(),
  /**
   * Since when it sleeps, all its people away (OP-1, ADR 0084): it owes nothing, makes nothing and
   * wears nothing until one of them comes back. Absent, awake.
   */
  asleepSince: z.number().int().nonnegative().optional(),
  /**
   * A fixed lot's ground (C9, ADR 0068): the scene id of the town layout laid on it, as a town's
   * plot was. A fixed lot is protected (GV-P1), owes nothing and never falls. Absent, any other lot.
   */
  layout: z.string().optional(),
  /**
   * Who controls it while a siege holds it (SIEGE H2c, R-69, SG-4: a small site whose core fell
   * under its threshold in a Major Pulse): it uses it with its rights, its owner keeps the
   * property and loses the use. Absent, its owner controls it.
   */
  occupier: LotOwnerSchema.optional(),
});
export type LotState = z.infer<typeof LotStateSchema>;

/**
 * Where a gate with a lock stands (BUILD, PT-1): open until when (game time; null: not opened), and
 * whether it stands shut (bolted, not forced, not ruined, not open). What a gate's change travels as
 * (`gate`, `gateChanged`), instead of its whole lot.
 */
export interface GateState {
  readonly until: number | null;
  readonly closed: boolean;
}

/** A gate's state as its lot says it (null: no such piece). */
export function gateStateOf(lot: Pick<LotState, 'structures'>, building: string): GateState | null {
  const piece = lot.structures.find((s) => s.id === building);
  return piece ? { until: piece.openUntil ?? null, closed: piece.closed === true } : null;
}

/** A lot with one of its gates' state changed; the lot itself when it has no such piece. */
export function withGateState(lot: LotState, building: string, state: GateState): LotState {
  if (!lot.structures.some((s) => s.id === building)) return lot;
  return {
    ...lot,
    structures: lot.structures.map((piece) => {
      if (piece.id !== building) return piece;
      const { openUntil: _until, closed: _closed, ...rest } = piece;
      return {
        ...rest,
        ...(state.until !== null && { openUntil: state.until }),
        ...(state.closed && { closed: true }),
      };
    }),
  };
}

/** Who uses a lot now (SIEGE H2c, SG-4): its occupier while a siege holds it, else its owner. */
export const lotController = (lot: Pick<LotState, 'owner' | 'occupier'>): LotOwner =>
  lot.occupier ?? lot.owner;

/** Whether a lot (if known) is this character's own. */
export const ownedLot = (lot: Pick<LotState, 'owner'> | undefined, playerId: string): boolean =>
  lot !== undefined && ownsLot(lot.owner, playerId);

/**
 * The site a structure stands on (BUILD, HOME): a lot, a character's fixed lot among them (C9, ADR
 * 0068). Its tiles are relative to the lot's corner.
 */
export const SiteRefSchema = z.object({ kind: z.literal('lot'), id: LotIdSchema });
export type SiteRef = z.infer<typeof SiteRefSchema>;

/**
 * A raid on a home in lawless ground (LOCAL-LIFE LL-5): the power whose band took its goods, the
 * lair whose chest they went to (LR-2: anyone may go and take them back), and when (game time).
 */
export const LotRaidSchema = z.object({
  lot: LotIdSchema,
  power: z.string().min(1).max(64),
  place: z.string().min(1).max(64),
  goods: ItemAmountsSchema,
  at: z.number().int().nonnegative(),
});
export type LotRaid = z.infer<typeof LotRaidSchema>;
